import { Box, Paper, Typography } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useAppTranslation } from 'src/admin/core/hooks'
import { CriticalAlert } from 'src/admin/core/services/screens/screenDashboardService'

interface CriticalAlertsProps {
  alerts: CriticalAlert[]
}

const ALERT_COLORS: Record<string, { border: string; iconColor: string }> = {
  screen_offline: { border: '#ef4444', iconColor: '#ef4444' },
  crash_loop: { border: '#f97316', iconColor: '#f97316' },
}

const CriticalAlerts = ({ alerts }: CriticalAlertsProps) => {
  const { label } = useAppTranslation()

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        flex: 1,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '0.9rem' }}>
            {label('CRITICAL_ALERTS' as any)}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{ textTransform: 'uppercase', color: 'text.disabled', fontWeight: 600, fontSize: '0.62rem' }}
        >
          {label('LAST_24_HOURS' as any)}
        </Typography>
      </Box>

      {/* Alert Items */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {alerts.map((alert) => {
          const colors = ALERT_COLORS[alert.type] || ALERT_COLORS.screen_offline
          return (
            <Paper
              key={alert.id}
              elevation={0}
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                borderLeftWidth: 3,
                borderLeftColor: colors.border,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.5,
                transition: 'box-shadow 0.2s',
                '&:hover': { boxShadow: 1 },
              }}
            >
              <WarningAmberIcon sx={{ fontSize: 18, color: colors.iconColor, mt: 0.1, flexShrink: 0 }} />
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ fontSize: '0.82rem', color: colors.iconColor }}
                >
                  {alert.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
                >
                  {alert.detail}
                </Typography>
              </Box>
            </Paper>
          )
        })}

        {alerts.length === 0 && (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 2 }}>
            No hay alertas activas
          </Typography>
        )}
      </Box>
    </Paper>
  )
}

export default CriticalAlerts
